export type Role = "admin" | "operator" | "viewer";

export interface CurrentUser {
  id: number;
  username: string;
  email: string;
  role: Role;
  telegram_connected: boolean;
}

export interface User {
  id: number;
  username: string;
  email: string;
  role: Role;
  is_active: boolean;
  created_at: string | null;
}

export interface CreateUserInput {
  username: string;
  email: string;
  password: string;
  role: Role;
}

export interface UpdateUserInput {
  email?: string;
  role?: Role;
  password?: string;
  is_active?: boolean;
}

export interface Customer {
  id: number;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  city: string | null;
  branch: string | null;
  contract_number: string | null;
  contract_date: string | null;
  drive_folder_id: string | null;
  notes: string | null;
  is_active: boolean;
}

export interface Batch {
  id: number;
  code: string;
  supplier_name: string | null;
  model: string | null;
  firmware_version: string | null;
  received_date: string | null;
  notes: string | null;
  device_count: number;
  product_type_id?: number;
}

export type DeviceStatus =
  | "received"
  | "scanned"
  | "keyed"
  | "flashed"
  | "tested"
  | "labeled"
  | "assigned"
  | "shipped"
  | "delivered"
  | "returned"
  | "defect"
  | "restored";

export interface Device {
  id: number;
  batch_id: number;
  dev_eui: string;
  join_eui: string | null;
  app_key: string | null;
  model: string | null;
  firmware_version: string | null;
  serial_number: string | null;
  box_number: string | null;
  qr_payload?: string | null;
  status: DeviceStatus;
  shipment_id: number | null;
  return_box_id?: number | null;
  scanned_at?: string | null;
  product_type_id?: number;
  product_type_slug?: "module" | "antenna" | string;
  linked_device_id?: number | null;
  linked_dev_eui?: string | null;
}

export type ShipmentStatus = "draft" | "ready" | "shipped" | "delivered";

export interface Shipment {
  id: number;
  code: string;
  customer_id: number | null;
  status: ShipmentStatus;
  shipped_date: string | null;
  chirpstack_added_at: string | null;
  accounting_confirmed_at: string | null;
  location: string | null;
  branch: string | null;
  drive_export_url: string | null;
  device_count: number;
  type_counts?: { module: number; antenna: number };
  on_debt: boolean;
  delivery_type_id: number | null;
  delivery_type_name: string | null;
  delivery_type_slug: string | null;
  has_documents?: boolean;
  notes?: string | null;
}

export interface DeliveryType {
  id: number;
  name: string;
  slug: string | null;
  marks_debt: boolean;
  active: boolean;
  sort_order: number;
}

export interface StockItem {
  id: number;
  name: string;
  unit: string;
  quantity: number;
  low_threshold: number | null;
  low_alerted: boolean;
  active: boolean;
  sort_order: number;
  notes: string | null;
  is_low: boolean;
  /** Present on `GET /stock`: item appears in an active product recipe. */
  recipe_item?: boolean;
}

// ── Free stock issues (livrări libere) ──────────────────────────────────────
export interface Technician {
  id: number;
  full_name: string;
  phone: string | null;
  active: boolean;
  notes: string | null;
  created_at: string | null;
}

export type IssueRecipientType = "technician" | "customer";
export type StockIssueKind = "issue" | "return" | "close";
export type StockIssueCloseKind = "installed" | "installed_at_customer" | "written_off";

export interface IssueRecipient {
  type: IssueRecipientType;
  id: number;
  name: string;
  subtitle?: string;
}

export interface StockIssueLine {
  id: number;
  stock_item_id: number;
  stock_item_name: string | null;
  unit: string | null;
  quantity: number;
}

export interface StockIssue {
  id: number;
  code: string;
  kind: StockIssueKind;
  close_kind: StockIssueCloseKind | null;
  technician_id: number | null;
  customer_id: number | null;
  target_customer_id: number | null;
  target_customer_name: string | null;
  recipient: { type: IssueRecipientType; id: number; name: string | null };
  act_number: string | null;
  issued_at: string | null;
  note: string | null;
  user_id: number | null;
  accounting_required: boolean;
  accounting_confirmed_at: string | null;
  accounting_confirmed_by_id: number | null;
  /** 1 la creare; crește la fiecare modificare cu diferențe. */
  revision: number;
  edited_at: string | null;
  /** username-ul celui care a modificat ultima dată */
  edited_by: string | null;
  cancelled_at: string | null;
  /** username-ul celui care a anulat */
  cancelled_by: string | null;
  cancel_note: string | null;
  created_at: string | null;
  lines: StockIssueLine[];
}

/** O diferență după modificarea unei predări (`delta` vine ca șir zecimal, cu semn). */
export interface StockIssueChange {
  stock_item_id: number;
  name: string;
  unit: string | null;
  delta: string;
}

export interface HoldingRow {
  recipient: { type: IssueRecipientType; id: number; name: string | null };
  stock_item_id: number;
  name: string | null;
  unit: string | null;
  quantity: number;
}

export interface CreateStockIssueInput {
  kind: StockIssueKind;
  recipient: { type: IssueRecipientType; id: number };
  lines: { stock_item_id: number; qty: number }[];
  note?: string | null;
  close_kind?: StockIssueCloseKind;
  target_customer_id?: number | null;
}

export interface StockMovement {
  id: number;
  stock_item_id: number;
  delta: number;
  balance_after: number;
  reason: string;
  shipment_id: number | null;
  user_id: number | null;
  note: string | null;
  created_at: string;
  product_type_id?: number | null;
  moment?: RecipeMoment | null;
}

export type RecipeMoment = "scan" | "shipped" | "delivered";

export interface RecipeLine {
  id?: number;
  stock_item_id: number;
  stock_item_name?: string;
  unit?: string;
  qty: number;
  moment: RecipeMoment;
}

export interface ProductType {
  id: number;
  slug: string;
  name: string;
  is_system: boolean;
  active: boolean;
  sort_order: number;
  lines: RecipeLine[];
}

export interface ConsumptionRow {
  stock_item_id: number;
  stock_item_name: string;
  unit: string;
  product_type_id: number | null;
  product_type_name: string | null;
  consumed: number;
}

export interface ShipmentAttachment {
  id: number;
  shipment_id: number;
  original_filename: string;
  mime_type: string | null;
  size_bytes: number;
  note: string | null;
  uploaded_at: string | null;
}

export interface DriveStatus {
  configured: boolean;
  authenticated: boolean;
  expired?: boolean;
  can_refresh?: boolean;
  message?: string;
  credentials_path?: string;
  token_path?: string;
  error?: string;
}

export interface DriveFolder {
  id: string;
  name: string;
  parents?: string[];
  modifiedTime?: string;
}

export interface AntennaImportResult {
  created: number;
  invalid: string[];
  exists: string[];
  linked: number;
  link_not_found: string[];
}

export type ImportField = "dev_eui" | "join_eui" | "app_key" | "model" | "supplier_id";
export type ImportMapping = Record<ImportField, number | null>;

export interface ImportPreview {
  sheets: string[];
  sheet: string;
  /** 0-based index of the header row; -1 = no header. */
  header_row: number;
  columns: { index: number; header: string; samples: string[] }[];
  mapping: ImportMapping;
  rows: string[][];
  total_rows: number;
  suggested_join_eui: string | null;
}

export interface ImportMappingRequest {
  mapping: ImportMapping;
  header_row: number;
  sheet?: string;
  join_eui_default?: string;
  app_key_default?: string;
}

export interface ImportResult {
  inserted: number;
  skipped: number;
  total_rows: number;
  backfilled_serial?: number;
  skipped_by_batch?: Record<string, number>;
  issues: string[];
}

export interface UploadDriveResult {
  uploaded: boolean;
  file: { id: string; name: string; webViewLink: string; shared: boolean };
  shipment: Shipment;
}

export interface GodexStatus {
  configured: boolean;
  backend?: "win_spooler" | "linux_usb" | "tcp";
  target?: string;
  printer_present?: boolean;
  device_present?: boolean;
  available_printers?: string[];
  error?: string;
}

export type LabelType =
  | "device"
  | "box"
  | "partida_small"
  | "partida_large"
  | "link_small"
  | "link_large"
  | "passport_small"
  | "passport_large"
  | "stock_item";

export type FontPreset =
  | "scalable"
  | "bitmap_small"
  | "bitmap_medium"
  | "bitmap_large"
  | "monospace";

export type TextSource = "static" | "field";

export interface LabelTextElement {
  id: string;
  kind: "text";
  source: TextSource;
  content: string | null;
  field: string | null;
  x: number;
  y: number;
  font: FontPreset;
  scale: number;
  bold: boolean;
  rotation: 0 | 90 | 180 | 270;
}

export interface LabelQrElement {
  id: string;
  kind: "qr";
  source: "field";
  field: string;
  x: number;
  y: number;
  scale: number;
}

export type LabelElement = LabelTextElement | LabelQrElement;

export interface LabelBackground {
  id: string;
  name: string;
  filename: string;
  fit: "contain" | "cover" | "stretch";
  threshold: number;
}

export interface LabelTemplate {
  width_mm: number;
  height_mm: number;
  darkness: number;
  speed: number;
  backgrounds: LabelBackground[];
  default_background_id: string | null;
  elements: LabelElement[];
}

export interface GodexPrintResult {
  printed: boolean;
  backend: string;
  target: string;
  bytes: number;
}

export interface GodexPayloadResult {
  ezpl_b64: string;
  bytes: number;
}

export interface GodexJobsPayloadResult {
  jobs: { ezpl_b64: string; bytes: number }[];
  delay_ms: number;
  total: number;
  devices?: number;
}

export interface PassportCandidate {
  device_id: number;
  model: string | null;
  dev_eui: string;
  serial_number: string | null;
  shipment_code: string | null;
}

export interface AuditLogEntry {
  id: number;
  created_at: string;
  user_id: number | null;
  username: string | null;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  payload: unknown;
  ip_address: string | null;
}

export interface AuditLogList {
  logs: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditFilters {
  action?: string;
  entity_type?: string;
  user_id?: number | string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export interface AuditOptions {
  actions: string[];
  entity_types: string[];
}

export interface PeriodStats {
  range: { from: string; to: string };
  sections: {
    scanned: number;
    flashed: number;
    shipped: number;
    returned: number;
    defect: number;
  };
}

export interface DeliveryTypeBreakdownRow {
  slug: string | null;
  name: string | null;
  shipments: number;
  devices?: number;
}

export interface StatsOverview {
  counts: {
    customers: number;
    batches: number;
    shipments: number;
    devices: number;
    antennas?: number;
  };
  device_by_status: Record<string, number>;
  shipment_by_status: Record<string, number>;
  shipment_by_delivery_type: DeliveryTypeBreakdownRow[];
  activity_by_day: Record<string, Record<string, number>>;
  recent_events: {
    id: number;
    created_at: string;
    username: string | null;
    action: string;
    entity_type: string | null;
    entity_id: number | null;
  }[];
}

export interface DocInfo {
  present: boolean;
  size?: number;
  original_filename?: string | null;
  uploaded_at?: string | null;
  uploaded_by?: string | null;
}

export interface BulkConflict {
  query: string;
  dev_eui: string;
  in_shipment_code: string | null;
  in_shipment_customer?: string | null;
  in_shipment_index?: number | null;
  box_number?: string | null;
}

export interface CreateAntennasResult {
  created: { dev_eui: string; device_id: number }[];
  invalid: string[];
  exists: string[];
  applied: BulkAssignResult["applied"];
  conflicts: BulkConflict[];
  box_number: string | null;
}

export interface BulkAssignResult {
  applied: {
    query: string;
    dev_eui: string;
    device_id: number;
    product_type_slug?: "module" | "antenna" | string;
  }[];
  conflicts: {
    query: string;
    dev_eui: string;
    in_shipment_code: string | null;
    in_shipment_customer?: string | null;
    in_shipment_index?: number | null;
    box_number?: string | null;
  }[];
  not_found: string[];
  box_number: string;
}

export interface FirmwareFile {
  filename: string;
  supplier_id: string;
  size: number;
}

export interface FirmwareUploadResult {
  saved: FirmwareFile[];
  errors: { filename: string; error: string }[];
}

export interface FlashQueueDevice extends Device {
  flashed_at: string | null;
  firmware_filename: string | null;
  // For devices whose firmware lives in a different batch (auto-discovered
  // via quick-flash search across batches), this points at the batch the
  // file sits in. Defaults to the device's own batch when local.
  firmware_batch_id?: number | null;
  firmware_borrowed_from?: number | null;
}

export interface DeviceLookup {
  id: number;
  dev_eui: string;
  serial_number: string | null;
  status: string;
  flashed_at: string | null;
  firmware_filename: string | null;
  // Which batch the firmware file actually lives in (may be a different
  // batch than the device's own, see firmware_borrowed_from below). Used by
  // the flash flow to download the file from the right place. null if no
  // firmware found anywhere.
  firmware_batch_id?: number | null;
  // If the firmware is borrowed from another batch (not the device's own),
  // this is the source batch id — used purely as a visual hint.
  firmware_borrowed_from?: number | null;
}

export interface FlashHistoryEvent {
  id: number;
  created_at: string | null;
  device_id: number;
  serial_number: string | null;
  dev_eui: string;
  success: boolean;
  firmware_filename: string | null;
  username: string | null;
}

export type FlashEvent =
  | { type: "started"; device_id: number; firmware: string }
  | { type: "stage"; stage: string }
  | { type: "progress"; stage: string; percent: number }
  | { type: "log"; line: string }
  | { type: "done"; success: boolean; returncode: number; lines: string[] }
  | { type: "error"; message: string }
  | { type: "finished"; success: boolean; firmware: string };

export interface TimelineEntry {
  id: string | number;
  source?: "device" | "shipment" | "batch" | "shipment_history";
  action: string;
  created_at: string;
  username?: string | null;
  user?: string | null;
  payload: unknown;
  context?: {
    shipment_id?: number;
    shipment_code?: string;
    batch_code?: string;
  };
}

export interface ShipmentHistoryEntry {
  shipment: {
    id: number;
    code: string;
    status: ShipmentStatus;
    customer_name: string | null;
  } | null;
  entered_at: string;
  left_at: string | null;
  reason: string | null;
  moved_by: string | null;
}

export interface DeviceHistory {
  // From the audit trail. `device.flashed_at` only records a successful
  // flash, so for a module that only ever failed this is the useful part.
  flash_summary?: {
    attempts: number;
    failed: number;
    last_failed_at: string | null;
  } | null;
  device: Device & {
    qr_payload?: string | null;
    scanned_at?: string | null;
    flashed_at?: string | null;
    shipped_at?: string | null;
    notes?: string | null;
    created_at?: string;
  };
  batch: {
    id: number;
    code: string;
    supplier_name: string | null;
    received_date: string | null;
  } | null;
  shipment: {
    id: number;
    code: string;
    status: ShipmentStatus;
    shipped_date: string | null;
    location: string | null;
    branch: string | null;
    drive_export_url: string | null;
  } | null;
  current_shipment: {
    id: number;
    code: string;
    status: ShipmentStatus;
    shipped_date: string | null;
    location: string | null;
    branch: string | null;
    drive_export_url: string | null;
  } | null;
  customer: { id: number; name: string } | null;
  shipment_history: ShipmentHistoryEntry[];
  timeline: TimelineEntry[];
}

export type RmaStatus = "returned" | "defect" | "restored";

export interface RmaListEntry extends Device {
  updated_at: string | null;
  shipment_code: string | null;
  customer_name: string | null;
  batch_code: string | null;
  notes: string | null;
}

export interface DeviceLookupResult {
  found: boolean;
  dev_eui?: string;
  raw_query?: string;
  device?: Device;
  batch?: { id: number; code: string };
  shipment?: {
    id: number;
    code: string;
    status: ShipmentStatus;
    customer: string | null;
    customer_id?: number | null;
    index?: number | null;
  } | null;
  return_box?: {
    id: number;
    code: string;
    status: string;
  } | null;
  location?: "return_box" | "shipment" | "none";
}

export interface SavedDeviceLookup {
  device_id: number;
  dev_eui: string;
  model: string | null;
  status: string;
  current_shipment: { id: number; code: string } | null;
  customer_name: string | null;
  note: string | null;
  saved_at: string;
}

export interface ScanSessionSummary {
  id: number;
  name: string;
  mode: string | null;
  shipment_id: number | null;
  box_number: string | null;
  created_at: string | null;
  created_by: string | null;
  log_count: number;
}

export interface ScanSessionFull extends ScanSessionSummary {
  data: { logs?: unknown[] };
  shipment_code?: string;
}

export interface LocalAgentStatus {
  available: boolean;
  stm32: boolean;
  godex: boolean;
  godex_target?: string;
}

export interface AgentInfo {
  agent_id: string;
  name: string;
}

export interface ApiTokenStatus {
  has_token: boolean;
  generated_at: string | null;
}

export interface ShipmentSummary {
  id: number;
  code: string;
  device_count: number;
}

export interface ActModelLine {
  model: string;
  count: number;
}

export interface ActSuggest {
  same_customer: boolean;
  customer_id?: number;
  customer_name?: string;
  act_number_suggested: string;
  act_date_suggested: string;
  devices_total: number;
  boxes_total: number;
  shipments_summary: ShipmentSummary[];
  models: ActModelLine[];
  default_notes: string;
}

export interface ActPdfInput {
  shipment_ids: number[];
  act_number: string;
  act_date: string;
  notes: string;
}

export interface WorkerRole {
  id: number;
  name: string;
  active: boolean;
  created_at: string;
}

export interface Worker {
  id: number;
  full_name: string;
  phone: string | null;
  role_id: number | null;
  daily_rate?: number | null;   // omitted for non-admin responses
  notes: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkEntry {
  id: number;
  worker_id: number;
  work_date: string;            // YYYY-MM-DD
  start_time: string;           // HH:MM
  end_time: string;             // HH:MM
  amount?: number;              // omitted for non-admin responses
  note: string | null;
  created_by_id: number | null;
  created_at: string;
}

export interface WorkerPayment {
  id: number;
  worker_id: number;
  paid_at: string;              // YYYY-MM-DD
  amount: number;
  covers_from: string | null;
  covers_to: string | null;
  note: string | null;
  created_by_id: number | null;
  created_at: string;
}

export interface WorkersReportPerWorker {
  worker_id: number;
  full_name: string;
  role_name: string | null;
  days: number;
  hours: number;
  earned: number;
  paid: number;
  owed: number;
}

export interface WorkersReportPerRole {
  role_id: number | null;
  role_name: string | null;
  days: number;
  hours: number;
  earned: number;
}

export interface WorkersReportSummary {
  range: { from: string; to: string };
  grand_total: {
    days: number;
    hours: number;
    earned: number;
    paid: number;
    owed: number;
  };
  per_worker: WorkersReportPerWorker[];
  per_role: WorkersReportPerRole[];
}

export interface ShipmentStatsPerCustomer {
  customer_id: number | null;
  customer_name: string | null;
  shipments: number;
  devices: number;
  returns: number;
  defects: number;
}

export interface ShipmentStatsResponse {
  range: { from: string; to: string; date_field: "shipped" | "created" };
  filter: { customer_id: "all" | "null" | number };
  totals: {
    shipments: number;
    devices: number;
    returns: number;
    defects: number;
  };
  by_status: {
    draft: number;
    ready: number;
    shipped: number;
    delivered: number;
  };
  by_delivery_type: DeliveryTypeBreakdownRow[];
  internal_stock: {
    shipments: number;
    devices_unassigned: number;
  };
  per_customer: ShipmentStatsPerCustomer[];
}

export interface DeliveryPlanRow {
  plan_id: number | null;
  customer_id: number;
  customer_name: string | null;
  product_type_id?: number;
  period_month: string; // "YYYY-MM"
  planned: number;
  planned_original: number | null; // plan before it was changed (null = unchanged)
  delivered: number; // net, debt + returned modules excluded (incl. defect-exchange)
  delivered_debt: number; // debt modules — shown separately, not in the sum
  delivered_exchange: number; // subset of delivered from defect-exchange shipments
  billed: number; // modules in normal (non-exchange, non-debt) shipments, returns not subtracted — what meets the plan
  remaining: number; // planned - billed
  // Breakdown behind `delivered` (same customer + ship month):
  gross: number; // every module still in these shipments, returned or not
  gross_normal: number;
  gross_exchange: number;
  gross_debt: number;
  returned: number; // came back — subtracted from delivered
  returned_unprocessed: number; // subset of returned: in a return box, status not updated yet
  moved_out: number; // later moved into another customer's shipment (not counted here)
  foreign_returned: number; // modules not in the system, in this customer's return boxes received that month
}

export type PurchaseOrderStatus = "ordered" | "in_transit" | "at_customs" | "received";
export type ProcurementAttachmentKind = "invoice" | "payment" | "customs" | "tracking" | "other";

export interface Supplier {
  id: number;
  name: string;
  url: string | null;
  contact: string | null;
  active: boolean;
  created_at: string;
}

export interface PurchaseOrder {
  id: number;
  code: string;
  supplier_id: number;
  title: string | null;
  supplier_order_id: string | null;
  ordered_at: string;          // YYYY-MM-DD
  received_at: string | null;
  total_amount?: number | null;  // omitted for non-admin
  total_currency?: string | null;  // omitted for non-admin
  status: PurchaseOrderStatus;
  notes: string | null;
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrderLine {
  id: number;
  order_id: number;
  code: string | null;
  description: string | null;
  quantity: number;
  unit_price?: number | null;  // omitted for non-admin
  created_at: string;
}

export interface PurchaseOrderAttachment {
  id: number;
  order_id: number;
  kind: ProcurementAttachmentKind;
  original_filename: string;
  mime_type: string | null;
  size_bytes: number;
  note: string | null;
  uploaded_by_id: number | null;
  uploaded_at: string;
}

export interface PurchaseOrderTracking {
  id: number;
  order_id: number;
  tracking_number: string;
  carrier_code: number | null;
  status: string | null;
  last_polled_at: string | null;
  delivered_at: string | null;
  registered_at: string | null;
  note: string | null;
  last_location: string | null;
  last_event_description: string | null;
  last_event_at: string | null;        // ISO timestamp from 17track
  eta_from: string | null;             // YYYY-MM-DD
  eta_to: string | null;               // YYYY-MM-DD
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrderDetail {
  order: PurchaseOrder;
  lines: PurchaseOrderLine[];
  attachments: PurchaseOrderAttachment[];
  trackings: PurchaseOrderTracking[];
}

export type ExpensePaymentMethod = "cash" | "card" | "transfer";

export interface ExpenseCategory {
  id: number;
  name: string;
  active: boolean;
}

export interface ExpenseAttachment {
  id: number;
  expense_id: number;
  original_filename: string;
  mime_type: string | null;
  size_bytes: number;
  note: string | null;
  uploaded_at: string | null;
}

export interface Expense {
  id: number;
  category_id: number;
  category_name: string | null;
  spent_at: string;
  amount?: number | null;
  currency?: string | null;
  description: string | null;
  payment_method: ExpensePaymentMethod | null;
  paid_by: string | null;
}

export interface ExpenseDetail {
  expense: Expense;
  attachments: ExpenseAttachment[];
}

export interface ReturnBox {
  id: number;
  code: string;
  customer_id: number | null;
  customer_name?: string | null;
  received_at: string | null;
  notes: string | null;
  act_number: string | null;
  predator_note: string | null;
  transferred_shipment_id: number | null;
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
  total?: number;
  restored?: number;
  pending?: number;
}

export interface ReturnBoxAttachment {
  id: number;
  return_box_id: number;
  original_filename: string;
  mime_type: string | null;
  size_bytes: number;
  note: string | null;
  uploaded_at: string | null;
}

export interface ReturnBoxDeviceRow extends Device {
  model: string | null;
  serial_number: string | null;
}

export interface ForeignModule {
  id: number;
  return_box_id: number;
  code: string;
  created_at: string;
}

export interface ReturnBoxDetail {
  box: ReturnBox;
  devices: ReturnBoxDeviceRow[];
  foreign_modules?: ForeignModule[];
}

export const NOTIFICATION_TYPES = [
  "shipment.status_changed",
  "shipment.delivery_reminder",
  "shipment.accounting_confirmed",
  "shipment.chirpstack_confirmed",
  "procurement.tracking_update",
  "tracking.stuck",
  "chirpstack.reminder_due",
  "device.defect_spike",
  "stock.low",
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  body: string;
  link: string | null;
  data: Record<string, unknown> | null;
  created_at: string;
  read_at: string | null;
  delivered_telegram_at: string | null;
}

export interface NotificationListResponse {
  notifications: Notification[];
  unread_count: number;
}

export interface NotificationPreference {
  type: NotificationType;
  in_app: boolean;
  telegram: boolean;
}

export interface TelegramLinkStartResponse {
  url: string;
  expires_in: number;
}

export interface DriveSpreadsheet {
  id: string;
  name: string;
  modifiedTime?: string;
}

export interface SheetExportSkipped {
  dev_eui: string;
  model: string;
  shipment_code: string;
}

export interface SheetExportResult {
  appended: number;
  updated: number;
  skipped: SheetExportSkipped[];
  spreadsheet_name: string;
  tab: string;
}

export interface TelegramTestResponse {
  ok: boolean;
  info?: { id: number; username: string; first_name: string };
  error?: string;
  status?: number;
}

export interface CustomerStatTotals {
  delivered: number; at_client: number; returned: number; in_return_box: number;
  defect: number; restored: number; on_debt: number; exchange: number;
}
export interface CustomerPlanCell { planned: number; delivered: number; remaining: number; }
export interface CustomerStatRow {
  customer_id: number; name: string | null;
  totals: CustomerStatTotals; plan_month: CustomerPlanCell; plan_year: CustomerPlanCell;
}
export interface CustomersStatsResponse {
  customers: CustomerStatRow[];
  total: { totals: CustomerStatTotals; plan_month: CustomerPlanCell; plan_year: CustomerPlanCell };
}
export interface CustomerMonthlyRow {
  period_month: string; planned: number; delivered: number; billed: number; delivered_debt: number; remaining: number;
}
export interface CustomerStatShipment {
  id: number; code: string; shipped_date: string | null; device_count: number;
  delivery_type_slug: string | null; delivery_type_name: string | null; on_debt: boolean;
}
export interface CustomerStatDetail {
  customer: { id: number; name: string };
  totals: CustomerStatTotals; plan_month: CustomerPlanCell; plan_year: CustomerPlanCell;
  monthly: CustomerMonthlyRow[]; shipments: CustomerStatShipment[];
}
